import React, { useCallback, useMemo, useState } from 'react';
import { mobileConfig } from '../../commons/config';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';
import { useMutation } from '@tanstack/react-query';
import useApi from '../../api/backendApiContext';
import { ConfirmDialogProps, ConfirmModal } from '../../commons/components/ConfirmModal';
import { useNotification } from '../../notification/notificationsContext';
import { errorToString } from '../../util/utils';
import facebookInstagramImage from '../../assets/images/facebook-instagram.png';
import { IntegrationTile } from '../../commons/components/IntegrationTile';
import { ExternalBrowserDialog } from '../../commons/components/ExternalBrowserDialog';
import { notConnectedFacebookAccount } from '../../social-posting/socialPostingApi';
import { useFetchedFacebookAccount } from '../hooks/useFetchedFacebookAccount';
import { useConnectFacebook } from '../hooks/useConnectFacebook';

export const FacebookSettings = () => {
  const analytics = useAnalytics();

  const {
    query: { isLoading, isError, data },
    queryStatusComponent,
  } = useFetchedFacebookAccount();
  const { socialPostingApi } = useApi();
  const { notifySuccess, notifyError } = useNotification();

  const isActive = !!data && !data.isDisconnected;
  const isDisconnected = !!data && data.isDisconnected;

  const queryCacheManager = useQueryCacheManager();

  const [openBrowserKey, setOpenBrowserKey] = useState<string | null>(null);
  const { startConnect, isConnecting } = useConnectFacebook();
  const onConnectClick = useCallback(() => {
    if (mobileConfig().isMobile) {
      setOpenBrowserKey(new Date().toString());
      return;
    }
    startConnect();
  }, [startConnect]);

  const { isLoading: isDisconnecting, mutate: disconnect } = useMutation(
    async () => socialPostingApi.disconnectFacebook(),
    {
      onSuccess: () => {
        queryCacheManager.setFacebookAccount(notConnectedFacebookAccount);
        const message = 'Facebook has been successfully disconnected.';
        notifySuccess({
          notificationMsg: message,
          logMsg: message,
        });
        analyticsTrack(analytics, AnalyticsEvent.FACEBOOK_DISCONNECTED);
      },
      onError: (e) => {
        const message = errorToString(
          e,
          'Something when wrong while disconnecting from Facebook. Please try again.'
        );
        notifyError({
          notificationMsg: message,
          logMsg: message,
        });
      },
    }
  );

  const [confirmDisconnectModal, setConfirmDisconnectModal] = useState(false);

  const onDisconnectConfirmed = useCallback(() => {
    setConfirmDisconnectModal(false);
    disconnect();
  }, [disconnect]);

  const disconnectConfirmDialogProps: ConfirmDialogProps = useMemo<ConfirmDialogProps>(
    () => ({
      title: 'Disconnect Growegy and Facebook & Instagram?',
      message: 'You’ll be able to reconnect at any time.',
      footer: {
        type: 'show',
        applyButton: {
          label: 'Disconnect',
          variant: 'danger',
          onClick: onDisconnectConfirmed,
        },
        cancelButton: {
          label: 'Cancel',
          onClick: () => setConfirmDisconnectModal(false),
        },
      },
    }),
    [onDisconnectConfirmed]
  );

  return (
    <>
      <IntegrationTile
        isActive={isActive}
        isDisconnected={isDisconnected}
        isConnecting={isConnecting}
        isDisconnecting={isDisconnecting}
        isSchedulingResync={false}
        isLoading={isLoading}
        isError={isError}
        queryStatusComponent={queryStatusComponent}
        title="Facebook & Instagram"
        subtitle={data ? data.name : null}
        privacyPolicyUrl="https://www.growegy.com/resources/about/privacy-policy/"
        learnMoreViewedEvent={AnalyticsEvent.FACEBOOK_HELP_VIEWED}
        onConnectClick={onConnectClick}
        onDisconnectClick={() => setConfirmDisconnectModal(true)}
        icon={
          <img
            className="integration-row-icon"
            src={facebookInstagramImage}
            alt="Facebook & Instagram"
          />
        }
      >
        <div>Generate content in Growegy and schedule new posts to Facebook and Instagram.</div>
      </IntegrationTile>
      {confirmDisconnectModal && <ConfirmModal {...disconnectConfirmDialogProps} />}
      <ExternalBrowserDialog dialogKey={openBrowserKey} title="Connect to Facebook and Instagram">
        To integrate your Facebook pages and Instagram accounts with Growegy, you'll be redirected
        to an external browser to securely log in and complete the connection. Tap Proceed to
        continue.
      </ExternalBrowserDialog>
    </>
  );
};
